import * as React from "react"

import WaveSurfer from "wavesurfer.js"
import CursorPlugin from "wavesurfer.js/dist/plugin/wavesurfer.cursor.min.js"
import RegionPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.min.js"

import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"

import startEndTimeValid from "../../../../../../helpers/audio/startAndEndTimeValid"
import durationToMSString from "../../../../../../helpers/soundFile/durationToMSString"
import roundToThreeDecimalPlaces from "../../../../../../helpers/strings/roundToThreeDecimalPlaces"
import { lightTheme } from "../../../../../../lib/muiTheme"
import Pad from "../../../../../../types/Pad"

const DEFAULT_COLOR = lightTheme.palette.primary.main

interface StartEndPickerProps {
  open: boolean
  pad: Pad
  disabled?: boolean
  startTime: number
  endTime: number
  onStartEndChange: (startTime: number | "", endTime: number | "") => void
}

export default function StartEndPicker({
  open,
  pad,
  disabled,
  startTime,
  endTime,
  onStartEndChange,
}: StartEndPickerProps) {
  const [loading, setLoading] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)
  const [wavesurf, setWavesurf] = React.useState<WaveSurfer | null>(null)
  const [rawDuration, setRawDuration] = React.useState<number | null>(null)
  const waveContainerId = `waveform-${pad.uuid}`
  const waveColor = pad.color === "#fff" ? "#a361c1" : pad.color || "#a361c1"

  const soundfile = pad && pad.soundfile
  const audioUrl = soundfile && soundfile.fileUrl

  const drawArea = React.useCallback(() => {
    if (!wavesurf) return
    if (!rawDuration) return

    wavesurf.regions.clear()

    if (startEndTimeValid(startTime, endTime, rawDuration)) {
      wavesurf.addRegion({
        start: startTime,
        end: endTime,
      })
    }
  }, [endTime, rawDuration, startTime, wavesurf])

  const initialiseWaveform = React.useCallback(() => {
    setLoading(true)

    const cursorPlugin = CursorPlugin.create({
      showTime: true,
      opacity: 1,
      customShowTimeStyle: {
        "background-color": "#000",
        color: "#fff",
        padding: "2px",
        "font-size": "10px",
      },
    })
    const regionPlugin = RegionPlugin.create({
      dragSelection: true,
    })

    const wavesurfer = WaveSurfer.create({
      container: `#${waveContainerId}`,
      waveColor,
      progressColor: waveColor,
      cursorWidth: 0,
      plugins: [cursorPlugin, regionPlugin],
    })

    // clean up previous region when we create new one
    let prevRegionId: number
    const handleRegionCreated = (event: unknown) => {
      const thisRegionId = (event as { id: number }).id
      if (prevRegionId) {
        const region = wavesurfer.regions.list[prevRegionId]
        if (region) region.remove()
      }
      prevRegionId = thisRegionId
    }

    const handleRegionUpdated = (event: unknown) => {
      const startTime = (event as { start: number }).start
      const endTime = (event as { end: number }).end
      onStartEndChange(startTime, endTime)
    }

    wavesurfer.on("region-created", handleRegionCreated)
    wavesurfer.on("region-update-end", handleRegionUpdated)

    wavesurfer.on("ready", () => {
      setLoaded(true)
      setLoading(false)
      setRawDuration(wavesurfer.getDuration())
    })

    wavesurfer.load(audioUrl || "")

    setWavesurf(wavesurfer)
  }, [waveContainerId, waveColor, audioUrl, onStartEndChange])

  // draw area
  React.useEffect(() => {
    drawArea()
  }, [drawArea, endTime, rawDuration, startTime, wavesurf])

  // initialise the waveform
  React.useEffect(() => {
    if (!open) return
    try {
      if (!loading && !loaded && soundfile) {
        initialiseWaveform()
      }
    } catch (err) {
      console.error("error initializing waveform", err)
    }
  }, [loaded, loading, initialiseWaveform, soundfile, open])

  React.useEffect(() => {
    if (open) return
    try {
      wavesurf && wavesurf.destroy()
    } catch (err) {
      console.debug("Wavesurf destroy failed as expected", err)
    }
  }, [open, wavesurf])

  if (!soundfile || disabled)
    return (
      <Box>
        <Typography variant="body1">
          Please save after selecting new sound.
        </Typography>
      </Box>
    )

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1">
          Use the area below to select start and end points for your sound.
        </Typography>
      </Box>

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        align-items="center"
      >
        <TextField
          type="number"
          variant="filled"
          size="small"
          style={{ maxWidth: "110px" }}
          InputLabelProps={{ shrink: true }}
          placeholder="0"
          InputProps={{
            endAdornment: <InputAdornment position="end">s</InputAdornment>,
            inputMode: "numeric",
            // pattern: "[0-9]*",
          }}
          value={roundToThreeDecimalPlaces(startTime) || ""}
          label="Start time"
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => onStartEndChange(Number(e.target.value), endTime)}
        />
        <TextField
          type="number"
          variant="filled"
          size="small"
          style={{ maxWidth: "110px" }}
          InputLabelProps={{ shrink: true }}
          placeholder={durationToMSString(Number(rawDuration))}
          InputProps={{
            endAdornment: <InputAdornment position="end">s</InputAdornment>,
            inputMode: "numeric",
            // pattern: "[0-9]*",
          }}
          value={roundToThreeDecimalPlaces(endTime) || ""}
          label="End time"
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => onStartEndChange(startTime, Number(e.target.value))}
        />
      </Box>

      <div style={{ position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress
              style={{ color: (pad && pad.color) || DEFAULT_COLOR }}
              sx={{ my: 2 }}
            />
          </Box>
        )}

        <div className="wave-container-container">
          <div
            className="wave-container"
            id={waveContainerId}
            style={{
              border: `1px solid ${(pad && pad.color) || DEFAULT_COLOR}`,
              marginTop: "-1px",
            }}
          />
          {/* <div className='progress-line' style={{left: `${progressDecimal * 100}%`}} /> */}
        </div>
      </div>
    </Box>
  )
}
